import React, { useState, memo, useMemo, useContext, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { LOGOS, ICONS } from '../../constants/imageConstants';
import { AuthContext } from '../../context/AuthContext';
import { openConfirmationModal } from '../../reducers/uiSlice';
import { confirmationService } from '../../services/confirmationService';

const ProfileIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
  </svg>
);

const LogoutIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
  </svg>
);

const MobileHeader = memo(() => {
  const [showDropdown, setShowDropdown] = useState(false);
  const { user, logout, organization } = useContext(AuthContext);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const dropdownRef = useRef(null);

  const displayName = useMemo(() => {
    if (user && user.first_name) return user.first_name;
    if (user && user.email) return user.email.split('@')[0];
    return 'User';
  }, [user]);

  const orgLogo = useMemo(() => (organization && organization.logo) || LOGOS.NGO, [organization]);
  const orgName = useMemo(() => (organization && organization.name) || 'Terriqon', [organization]);

  useEffect(() => {
    const handleClickOutside = event => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showDropdown]);

  const handleProfileClick = () => {
    setShowDropdown(false);
    navigate('/profile');
  };

  const handleLogout = () => {
    setShowDropdown(false);
    const confirmId = 'header-logout';
    confirmationService.register(confirmId, () => logout());
    dispatch(
      openConfirmationModal({
        title: 'Confirm logout?',
        message: 'All offline and unsynced data will be permanently lost.',
        type: 'logout',
        confirmId
      })
    );
  };

  return (
    <header className="header-base relative">
      <div
        className="flex items-center gap-3 cursor-pointer active:opacity-80 transition-opacity"
        onClick={() => navigate('/dashboard')}
      >
        <img
          src={orgLogo}
          alt="Logo"
          className="w-9 h-9 object-contain"
          onError={e => { e.target.src = LOGOS.NGO; }}
        />
        <span className="text-white text-base font-extrabold tracking-wide uppercase select-none truncate max-w-[180px] drop-shadow-sm">
          {orgName}
        </span>
      </div>

      <div className="relative" ref={dropdownRef}>
        <button
          className="flex items-center gap-2 bg-white/10 hover:bg-white/20 rounded-full pl-1 pr-3 py-1 border border-white/20"
          onClick={() => setShowDropdown(prev => !prev)}
        >
          <img
            src={user && (user.profile_image_local || user.profile_image) || ICONS.USER_AVATAR}
            alt="Profile"
            className="w-7 h-7 rounded-full object-cover border border-white/50"
            onError={e => { e.target.src = ICONS.USER_AVATAR; }}
          />
          <span className="text-white text-sm font-semibold max-w-[80px] truncate">
            {displayName}
          </span>
          <img
            src={ICONS.CHEVRON_DOWN}
            alt=""
            className={`w-3 h-3 text-white transition-transform duration-200 ${showDropdown ? 'rotate-180' : ''}`}
            style={{ filter: 'brightness(0) invert(1)' }}
          />
        </button>

        {showDropdown && (
          <div className="absolute right-0 top-full mt-2 w-40 bg-white rounded-xl shadow-xl border border-gray-100 z-50 overflow-hidden animate-fade-in origin-top-right">
            <button
              onClick={handleProfileClick}
              className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center gap-3 transition-colors text-gray-700 active:bg-gray-100"
            >
              <ProfileIcon />
              <span className="font-medium text-sm">My Profile</span>
            </button>
            <button
              onClick={handleLogout}
              className="w-full px-4 py-3 text-left hover:bg-red-50 flex items-center gap-3 transition-colors text-red-600 border-t border-gray-50 active:bg-red-100"
            >
              <LogoutIcon />
              <span className="font-medium text-sm">Logout</span>
            </button>
          </div>
        )}
      </div>
    </header>
  );
});

export default MobileHeader;